import React from 'react';
import TaskCard from './components/TaskCard';
import { jobApplicationHistoryStore } from '../pipeline/components/JobApplicationHistoryStore';
import { observer } from 'mobx-react';
import HistoryCard from './components/HistoryCard';
import { Pagination } from 'ats/components/pagination/Pagination';
import { ListStore } from 'shared/stores/ListStore';
import { eventManager } from 'ats/utils/EventManager';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import ApplicantProfile from '../applicants/ApplicantProfile';
import EmptyState from 'ats/components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import { modalStore } from 'components/layout/modals/ModalStore';
import { AddApplicantsModal } from './components/AddApplicantsModal';
import PinnedTasksPanel from 'ats/components/pinned-tasks-panel/PinnedTasksPanel';
import { PIN_SCOPES } from 'ats/utils/taskPin';
import {
	TASK_FOCUS_EVENT,
	focusTimelineTask,
	isFocusRequestFor
} from 'ats/utils/timelineTaskFocus';

@observer
export class JobPositionHistory extends React.Component {
	constructor(props) {
		super(props);
		this.listStore = new ListStore(jobApplicationHistoryStore, {
			entityName: 'Job Application History'
		});
		this.timelineRef = React.createRef();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.jobPosition !== this.props.jobPosition) {
			jobApplicationHistoryStore.resetQuery();
			jobApplicationHistoryStore.setQuery('jobId', this.props.jobPosition.id);
			jobApplicationHistoryStore.setQuery('sort', {
				column: 'createdAt',
				asc: false
			});
			this.listStore.search();
		}
	}

	handleJobApplicationStageChanged = ({ jobId }) => {
		if (jobId === this.props.jobPosition.id) {
			this.listStore.search();
		}
	};

	handleTaskPinChanged = () => {
		this.listStore.search();
	};

	componentDidMount() {
		eventManager.on(
			'jobApplicationStageChanged',
			this.handleJobApplicationStageChanged
		);
		eventManager.on('taskPinChanged', this.handleTaskPinChanged);
		eventManager.on(TASK_FOCUS_EVENT, this.handleTaskFocusRequested);
	}

	componentWillUnmount() {
		eventManager.off(
			'jobApplicationStageChanged',
			this.handleJobApplicationStageChanged
		);
		eventManager.off('taskPinChanged', this.handleTaskPinChanged);
		eventManager.off(TASK_FOCUS_EVENT, this.handleTaskFocusRequested);
	}

	handleTaskFocusRequested = request => {
		if (
			!isFocusRequestFor(request, PIN_SCOPES.JOB, this.props.jobPosition?.id)
		) {
			return;
		}
		focusTimelineTask({
			container: this.timelineRef.current,
			listStore: this.listStore,
			taskId: request.taskId
		});
	};

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	handleApplicantNameClick = applicantId => {
		drawerStore.openDrawer(
			{
				containerId: ['job-positions', 'pipeline', 'applicants', 'clients'],
				drawerId: drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				showHeader: false,
				props: {
					applicantId: applicantId,
					onSuccess: () => {
						this.props.onSuccess && this.props.onSuccess();
						this.listStore.search();
					},
					onClose: () => {
						drawerStore.closeDrawer();
					}
				}
			},
			ApplicantProfile
		);
	};

	openAddCandidateModal = () => {
		modalStore.openModal(
			{ title: 'Add Candidate', size: 'md', position: 'center' },
			<AddApplicantsModal
				job={this.props.jobPosition}
				handleSuccess={() => {
					this.props.onSuccess && this.props.onSuccess();
					this.listStore.search();
				}}
			/>
		);
	};

	render() {
		const hasHistory = this.listStore.data.length > 0;

		return (
			<div className="activity-with-pinned">
				<div className="activity-with-pinned-main" ref={this.timelineRef}>
					{this.renderTimeline(hasHistory)}
				</div>
				{hasHistory ? (
					<PinnedTasksPanel
						scope={PIN_SCOPES.JOB}
						entityId={this.props.jobPosition?.id}
					/>
				) : null}
			</div>
		);
	}

	renderTimeline(hasHistory) {
		return (
			<div className="job-position-history">
				{hasHistory ? (
					<>
						<div className="job-position-history-list">
							{this.listStore.data.map(item =>
								item.source === 'task' ? (
									<TaskCard
										task={item}
										onSuccess={() => {
											this.listStore.search();
										}}
										onApplicantNameClick={this.handleApplicantNameClick}
									/>
								) : (
									<HistoryCard
										history={item}
										onSuccess={() => {
											this.listStore.search();
										}}
										onApplicantNameClick={this.handleApplicantNameClick}
									/>
								)
							)}
						</div>
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							onPaginationChange={this.handlePaginationChange}
						/>
					</>
				) : (
					<EmptyState
						icon={EMPTY_STATE_ICONS.sheet}
						iconAlt="No job applications"
						message="No applications or tasks found for this job position."
						actionText="Add Candidate"
						onActionClick={this.openAddCandidateModal}
					/>
				)}
			</div>
		);
	}
}
